// src/business/pages/hr/HRSeparation.jsx
// HR revamp Batch 5, migrated in UI-3E: the one place to separate an employee, follow a scheduled separation, end
// the onboarding of someone who never started, and read a former employee's separation record.
// The server decides everything (authority, clearance, completion); this page shows its answer.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { businessApi } from "../../../services/api";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import { formatDay } from "./myHrText";
import {
  Button, ConfirmDialog, ErrorState, Field, Input, Notice, PageHeader, Select, SkeletonRows, Surface, Textarea, plainError,
} from "../../../components/ui";
import {
  CLEARANCE_GROUPS, REASON_TYPES, outcomeMessage, primaryActionFor, readableDates, reasonTypeLabel, statusSummary,
} from "./separationText";

const longDay = (value) => formatDay(value, { year: true });

function Facts({ items }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      {items.filter(Boolean).map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-meta text-secondary">{label}</dt>
          <dd className="mt-0.5 break-words text-body text-foreground">{value || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function Panel({ title, action, children, labelledBy }) {
  return (
    <Surface
      as="section"
      aria-labelledby={labelledBy}
      toolbar={(
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id={labelledBy} className="text-panel-title text-foreground">{title}</h2>
          {action}
        </div>
      )}
    >
      <div className="p-4">{children}</div>
    </Surface>
  );
}

function ClearanceList({ clearance, loading, onRecheck }) {
  if (!clearance) return null;
  const summary = clearance.canComplete
    ? "Nothing is blocking this separation."
    : [
        clearance.blockedCount ? `${clearance.blockedCount} to resolve` : null,
        clearance.decideCount ? `${clearance.decideCount} to decide` : null,
      ].filter(Boolean).join(", ") + ".";
  return (
    <Surface
      as="section"
      aria-labelledby="clearance-title"
      aria-busy={loading}
      toolbar={(
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 id="clearance-title" className="text-panel-title text-foreground">Offboarding checklist</h2>
            <p className="text-body-sm text-secondary">For a last working day of {longDay(clearance.lastWorkingDate)}. {summary}</p>
          </div>
          <Button size="sm" icon={RefreshCw} loading={loading} onClick={onRecheck}>Re-check</Button>
        </div>
      )}
    >
      <div className="divide-y divide-subtle">
        {CLEARANCE_GROUPS.map((group) => {
          const items = clearance.items.filter((item) => item.group === group.key);
          if (!items.length) return null;
          return (
            <div key={group.key} className="p-4">
              <h3 className={`text-body font-semibold ${group.key === "blocked" ? "text-danger" : "text-foreground"}`}>
                {group.title} <span className="font-normal tabular-nums text-tertiary">{items.length}</span>
              </h3>
              <p className="text-meta text-tertiary">{group.hint}</p>
              <ul className="mt-2 divide-y divide-subtle">
                {items.map((item) => (
                  <li key={item.key} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                    <div className="min-w-0">
                      <div className="text-body text-foreground">{readableDates(item.title)}</div>
                      {item.detail ? <div className="text-body-sm text-secondary">{readableDates(item.detail)}</div> : null}
                    </div>
                    {item.action ? (
                      <Link to={item.action.to} className="shrink-0 text-body-sm font-medium text-accent hover:underline">{item.action.label}</Link>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </Surface>
  );
}

/** A confirmation that needs a written reason (cancel a separation, end onboarding). */
function ReasonDialog({ title, facts, consequence, confirmLabel, cancelLabel, danger, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  return (
    <ConfirmDialog
      title={title}
      facts={facts}
      consequence={consequence}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      tone={danger ? "danger" : "default"}
      confirmDisabled={trimmed.length < 5}
      onConfirm={() => onConfirm(trimmed)}
      onClose={onClose}
    >
      <Field label="Reason" required hint="At least 5 characters. Kept in the employee's history.">
        <Textarea rows={3} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
    </ConfirmDialog>
  );
}

export default function HRSeparation() {
  const { memberId } = useParams();
  const auth = useBusinessAuth();
  const isOwnerViewer = String(auth?.role || "").toUpperCase() === "OWNER";

  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ reasonType: "", reason: "", remarks: "", lastWorkingDate: "" });
  const requestRef = useRef(0);

  const load = useCallback(async (lastWorkingDate) => {
    const ticket = ++requestRef.current;
    setChecking(true);
    try {
      const res = await businessApi.getMemberSeparation(memberId, lastWorkingDate ? { last_working_date: lastWorkingDate } : {});
      if (ticket !== requestRef.current) return;
      setView(res);
      setLoadError("");
      setForm((prev) => (prev.lastWorkingDate ? prev : { ...prev, lastWorkingDate: res.today }));
    } catch (err) {
      if (ticket !== requestRef.current) return;
      setLoadError(plainError(err, "Check your connection and try again."));
    } finally {
      if (ticket === requestRef.current) {
        setChecking(false);
        setLoading(false);
      }
    }
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  const member = view?.member || null;
  const separation = view?.separation || null;
  const today = view?.today || "";
  const status = String(member?.status || "").toLowerCase();
  const isFormer = status === "terminated" || status === "left";
  const isScheduled = separation?.status === "scheduled";
  const isPreActivation = status === "on_hold";
  const canSeparate = !isFormer && !isScheduled && !isPreActivation && !member?.isOwner && ["active", "suspended"].includes(status);

  // Re-check the checklist when the chosen last working day changes (new separations only).
  useEffect(() => {
    if (!canSeparate || !form.lastWorkingDate || form.lastWorkingDate === view?.clearance?.lastWorkingDate) return undefined;
    const timer = setTimeout(() => load(form.lastWorkingDate), 350);
    return () => clearTimeout(timer);
  }, [canSeparate, form.lastWorkingDate, view?.clearance?.lastWorkingDate, load]);

  const action = useMemo(() => primaryActionFor({
    lastWorkingDate: form.lastWorkingDate,
    today,
    canComplete: Boolean(view?.clearance?.canComplete),
  }), [form.lastWorkingDate, today, view?.clearance?.canComplete]);

  const formValid = form.reasonType && form.reason.trim().length >= 5 && form.lastWorkingDate;
  const backTo = `/business/hr/employees/${memberId}`;
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const automatic = (view?.clearance?.items || []).filter((item) => item.group === "automatic");
  const openItems = (view?.clearance?.items || []).filter((item) => item.group === "blocked" || item.group === "decide");

  const recordSeparation = async () => {
    const res = await businessApi.recordSeparation(memberId, {
      reason_type: form.reasonType,
      reason: form.reason.trim(),
      remarks: form.remarks.trim() || undefined,
      last_working_date: form.lastWorkingDate,
    });
    setModal(null);
    setView(res);
    setNotice({ tone: res.outcome === "completed" ? "success" : "neutral", text: outcomeMessage(res.outcome, member.name, form.lastWorkingDate) });
  };

  const completeNow = async () => {
    try {
      const res = await businessApi.completeSeparation(separation.id);
      setModal(null);
      setView(res);
      setNotice({ tone: "success", text: `${member.name} has been separated.` });
    } catch (err) {
      load();
      throw err;
    }
  };

  const cancelScheduled = async (reason) => {
    const res = await businessApi.cancelSeparation(separation.id, { reason });
    setModal(null);
    setView(res);
    setForm((prev) => ({ ...prev, lastWorkingDate: res.today }));
    setNotice({ tone: "success", text: `The separation was cancelled. ${member.name} stays employed.` });
  };

  const endOnboarding = async (reason) => {
    await businessApi.endOnboarding(memberId, { reason });
    setModal(null);
    await load();
    setNotice({ tone: "success", text: `Onboarding ended. ${member.name} is no longer part of this business and can join another one.` });
  };

  const back = <Button as={Link} to={backTo} icon={ArrowLeft}>Employee record</Button>;

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Separate employee" actions={back} />
        <Surface><SkeletonRows rows={5} columns={2} label="Loading" /></Surface>
      </div>
    );
  }
  if (!view) {
    return (
      <div className="space-y-6">
        <PageHeader title="Separate employee" actions={back} />
        <Surface><ErrorState title="This separation did not load" description={loadError} onRetry={() => load()} /></Surface>
      </div>
    );
  }

  const endedOnboarding = status === "left" && !separation;
  const title = isFormer
    ? (endedOnboarding ? `${member.name}: onboarding ended` : `${member.name}: former employee`)
    : isPreActivation ? `End ${member.name}'s onboarding` : isScheduled ? `${member.name} is leaving` : `Separate ${member.name}`;
  const description = isFormer
    ? "How and when this person left. Their HR records stay available."
    : isPreActivation
      ? "They accepted the invitation but have not started work."
      : isScheduled
        ? statusSummary({ separation, today })
        : "Record why and when this employee leaves. The checklist shows what must happen first.";
  const showChecklist = !isFormer && !member.isOwner && !isPreActivation;

  return (
    <div className="space-y-6">
      <PageHeader title={title} description={description} actions={back} />
      {notice ? <Notice tone={notice.tone} role="status">{notice.text}</Notice> : null}
      {loadError ? <Notice tone="danger" role="alert">{loadError}</Notice> : null}

      <div className={showChecklist ? "grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" : "max-w-form space-y-6"}>
        <div className="min-w-0 space-y-6">
          {isFormer ? (
            <Panel title={endedOnboarding ? "Onboarding ended" : "Separated"} labelledBy="record-title">
              {separation && separation.status === "completed" ? (
                <Facts items={[
                  ["Last working day", longDay(separation.lastWorkingDate)],
                  ["Reason", reasonTypeLabel(separation.reasonType)],
                  ["What happened", separation.reason],
                  ["Completed", statusSummary({ separation, today })],
                  separation.remarks ? ["Remarks", separation.remarks] : null,
                  ["Recorded by", separation.scheduledByName],
                ]} />
              ) : endedOnboarding ? (
                <p className="text-body text-foreground">They left before starting work. The reason is in their employment history.</p>
              ) : (
                <Facts items={[
                  ["Last working day", member.lastWorkingDate ? longDay(member.lastWorkingDate) : "Not recorded"],
                  ["Reason", member.terminationReason || "Not recorded"],
                ]} />
              )}
              <p className="mt-4 text-body-sm text-secondary">Attendance, leave, schedule history and payslips stay in HR.</p>
            </Panel>
          ) : null}

          {member.isOwner ? (
            <Surface padded><p className="text-body text-foreground">The business owner cannot be separated.</p></Surface>
          ) : null}

          {isPreActivation ? (
            <Panel title="End onboarding" labelledBy="end-onboarding-title">
              <p className="text-body text-foreground">
                Ending onboarding cancels it, removes any schedule planned for them, and frees them to join another business.
                What they already submitted stays on record.
              </p>
              {isOwnerViewer ? (
                <Button className="mt-4" variant="danger" onClick={() => setModal("end-onboarding")}>End onboarding</Button>
              ) : (
                <p className="mt-3 text-body-sm text-secondary">Only the business owner can end onboarding.</p>
              )}
            </Panel>
          ) : null}

          {isScheduled ? (
            <Panel title={`Last working day: ${longDay(separation.lastWorkingDate)}`} labelledBy="scheduled-title">
              <Facts items={[
                ["Reason", reasonTypeLabel(separation.reasonType)],
                ["Recorded by", separation.scheduledByName],
                ["What happened", separation.reason],
                separation.remarks ? ["Remarks", separation.remarks] : null,
              ]} />
              {separation.lastAttempt?.blocked?.length ? (
                <Notice tone="warning" className="mt-4">
                  {separation.lastAttempt.mode === "automatic" ? "The automatic completion" : "The last attempt"} could not finish: {separation.lastAttempt.blocked.map(readableDates).join("; ")}.
                </Notice>
              ) : null}
              {isOwnerViewer ? (
                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  {separation.lastWorkingDate <= today ? (
                    <Button variant="danger" disabled={!view.clearance?.canComplete} onClick={() => setModal("complete")}>Complete separation</Button>
                  ) : null}
                  <Button onClick={() => setModal("cancel")}>Cancel separation</Button>
                </div>
              ) : (
                <p className="mt-3 text-body-sm text-secondary">Only the business owner can complete or cancel a separation.</p>
              )}
              {separation.lastWorkingDate <= today && !view.clearance?.canComplete ? (
                <p className="mt-2 text-meta text-tertiary">Complete it once the checklist is clear. It also completes on its own when everything is resolved.</p>
              ) : null}
            </Panel>
          ) : null}

          {canSeparate ? (
            isOwnerViewer ? (
              <Panel title="Separation details" labelledBy="separation-form-title">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Last working day"
                    required
                    hint={form.lastWorkingDate && form.lastWorkingDate > today ? "They keep working until the end of this day." : "Today or an earlier day separates them now."}
                  >
                    <Input type="date" value={form.lastWorkingDate} onChange={set("lastWorkingDate")} />
                  </Field>
                  <Field label="Reason" required>
                    <Select value={form.reasonType} onChange={set("reasonType")}>
                      <option value="">Choose a reason</option>
                      {REASON_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </Select>
                  </Field>
                  <Field label="What happened" required hint="At least 5 characters. Kept in their history." className="sm:col-span-2">
                    <Textarea rows={3} maxLength={500} value={form.reason} onChange={set("reason")} placeholder="e.g. Resigned to move to another city; resignation letter received Sep 30." />
                  </Field>
                  <Field label="Remarks (optional)" className="sm:col-span-2">
                    <Textarea rows={2} maxLength={1000} value={form.remarks} onChange={set("remarks")} />
                  </Field>
                </div>
                <div className="mt-4 flex flex-col gap-3 border-t border-subtle pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-body-sm text-secondary">
                    {action.mode === "waiting"
                      ? "Some items are still open, so the separation is recorded now and completes once they are done."
                      : action.mode === "schedule"
                        ? "Items still open now only need to be done by the last working day."
                        : "Everything is clear."}
                  </p>
                  <Button variant={action.mode === "now" ? "danger" : "primary"} disabled={!formValid || checking} onClick={() => setModal("confirm")}>
                    {action.label}
                  </Button>
                </div>
              </Panel>
            ) : (
              <Surface padded>
                <p className="text-body text-foreground">Only the business owner can separate employees. The checklist shows what would need to happen today.</p>
              </Surface>
            )
          ) : null}
        </div>

        {showChecklist ? (
          <div className="min-w-0">
            <ClearanceList clearance={view.clearance} loading={checking} onRecheck={() => load(canSeparate ? form.lastWorkingDate : undefined)} />
          </div>
        ) : null}
      </div>

      {modal === "confirm" ? (
        <ConfirmDialog
          title={action.mode === "schedule" ? `Schedule ${member.name}'s separation?` : action.mode === "now" ? `Separate ${member.name} now?` : `Record ${member.name}'s separation?`}
          facts={[
            { label: "Last working day", value: longDay(form.lastWorkingDate) },
            { label: "Reason", value: reasonTypeLabel(form.reasonType) },
            ...(openItems.length ? [{ label: "Still open", value: `${openItems.length} item${openItems.length === 1 ? "" : "s"} on the checklist` }] : []),
          ]}
          consequence={action.mode === "schedule"
            ? `${member.name} stays active until the end of ${longDay(form.lastWorkingDate)}. The separation completes the day after, once the checklist is clear.`
            : action.mode === "now"
              ? `${member.name} stops being an employee of this business now. This cannot be undone here.`
              : `The separation is recorded now and completes once the open items are done. ${member.name} stays active until then.`}
          confirmLabel={action.label}
          cancelLabel="Go back"
          tone={action.mode === "now" ? "danger" : "default"}
          onConfirm={recordSeparation}
          onClose={() => setModal(null)}
        >
          {automatic.length ? (
            <div>
              <p className="font-medium text-foreground">When it completes</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {automatic.map((item) => <li key={item.key}>{readableDates(item.title)}</li>)}
              </ul>
            </div>
          ) : null}
        </ConfirmDialog>
      ) : null}
      {modal === "complete" ? (
        <ConfirmDialog
          title={`Complete ${member.name}'s separation?`}
          facts={[
            { label: "Last working day", value: longDay(separation.lastWorkingDate) },
            { label: "Reason", value: reasonTypeLabel(separation.reasonType) },
          ]}
          consequence={`${member.name} stops being an employee of this business now. This cannot be undone here.`}
          confirmLabel="Complete separation"
          cancelLabel="Go back"
          tone="danger"
          onConfirm={completeNow}
          onClose={() => setModal(null)}
        >
          {automatic.length ? (
            <div>
              <p className="font-medium text-foreground">What happens now</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {automatic.map((item) => <li key={item.key}>{readableDates(item.title)}</li>)}
              </ul>
            </div>
          ) : null}
        </ConfirmDialog>
      ) : null}
      {modal === "cancel" ? (
        <ReasonDialog
          title={`Cancel ${member.name}'s separation?`}
          facts={[{ label: "Planned last working day", value: longDay(separation.lastWorkingDate) }]}
          consequence={`${member.name} stays employed and nothing else changes.`}
          confirmLabel="Cancel separation"
          cancelLabel="Keep it"
          onConfirm={cancelScheduled}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal === "end-onboarding" ? (
        <ReasonDialog
          title={`End ${member.name}'s onboarding?`}
          consequence="They leave this business before starting work. This cannot be undone here."
          confirmLabel="End onboarding"
          cancelLabel="Keep onboarding"
          danger
          onConfirm={endOnboarding}
          onClose={() => setModal(null)}
        />
      ) : null}
    </div>
  );
}
